import {
  AddReactionRequest,
  BulkRecognitionApprovalResult,
  CreateRecognitionRequest,
  FetchRecognitionRequest,
  Recognition,
  RecognitionPage,
  RejectRecognitionRequest,
  UpdateRecognitionRequest,
  recognitionService,
} from '@aslanpay/portal-common-lib';
import * as Sentry from '@sentry/react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useRef } from 'react';

// How often the pending list refreshes while a bulk approval is still being paid.
export const PROCESSING_REFETCH_INTERVAL_MS = 5000;

// How long the list keeps refreshing while the same recognitions stay processing. One stuck in
// APPROVING would otherwise keep the page polling for as long as it is open; reloading the page,
// or queueing more approvals, starts it again.
export const MAX_PROCESSING_POLL_MS = 10 * 60 * 1000;

// The bulk approve endpoint takes at most this many recognitions per request.
export const BULK_APPROVE_CHUNK_SIZE = 100;

const processingCountOf = (page?: RecognitionPage) =>
  page?.list.filter((recognition) => recognition.status === 'APPROVING').length ?? 0;

export const hasProcessingRecognitions = (page?: RecognitionPage) => processingCountOf(page) > 0;

const useListPendingApprovals = (params?: FetchRecognitionRequest, enabled = true) => {
  const queryClient = useQueryClient();
  const previousProcessingCount = useRef(0);
  const pollingSince = useRef<number>();
  const polledProcessingCount = useRef(0);

  const query = useQuery<RecognitionPage, Error>({
    queryKey: ['listPendingApprovals', params],
    queryFn: () => recognitionService.listPendingApprovals(params),
    enabled,
    refetchInterval: (data) => {
      const processingCount = processingCountOf(data);
      const hasMoreProcessing = processingCount > polledProcessingCount.current;
      polledProcessingCount.current = processingCount;

      if (processingCount === 0) {
        pollingSince.current = undefined;
        return false;
      }

      if (!pollingSince.current || hasMoreProcessing) {
        pollingSince.current = Date.now();
      }

      if (Date.now() - pollingSince.current < MAX_PROCESSING_POLL_MS) {
        return PROCESSING_REFETCH_INTERVAL_MS;
      }

      return false;
    },
  });

  // A processing recognition that has moved on has been paid (or sent back to pending), so the pot
  // balances and audit trail shown alongside it are out of date.
  useEffect(() => {
    const processingCount = processingCountOf(query.data);

    if (processingCount < previousProcessingCount.current) {
      queryClient.invalidateQueries({ queryKey: ['fetchPots'] });
      queryClient.invalidateQueries({ queryKey: ['fetchRecognitionAudit'] });
    }

    previousProcessingCount.current = processingCount;
  }, [query.data, queryClient]);

  return query;
};

const useCreateRecognition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateRecognitionRequest) => recognitionService.createRecognition(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listRecognitions'] });
      queryClient.invalidateQueries({ queryKey: ['listPendingApprovals'] });
    },
  });
};

const useGetRecognition = (recognitionId: string | undefined, enabled = true) => {
  return useQuery<Recognition, Error>({
    queryKey: ['getRecognition', recognitionId],
    queryFn: () => {
      if (!recognitionId) throw new Error('recognitionId is required');
      return recognitionService.getRecognition(recognitionId);
    },
    enabled: enabled && !!recognitionId,
  });
};

const useUpdateRecognition = (recognitionId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateRecognitionRequest) => {
      if (!recognitionId) throw new Error('recognitionId is required');
      return recognitionService.updateRecognition(recognitionId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getRecognition', recognitionId] });
      queryClient.invalidateQueries({ queryKey: ['listPendingApprovals'] });
    },
  });
};

const useDeleteRecognition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recognitionId: string) => recognitionService.deleteRecognition(recognitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listPendingApprovals'] });
    },
  });
};

const useApproveRecognition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recognitionId: string) => recognitionService.approveRecognition(recognitionId),
    onSuccess: (_data, recognitionId) => {
      queryClient.invalidateQueries({ queryKey: ['getRecognition', recognitionId] });
      queryClient.invalidateQueries({ queryKey: ['listPendingApprovals'] });
    },
  });
};

const toChunks = (ids: string[]) =>
  Array.from({ length: Math.ceil(ids.length / BULK_APPROVE_CHUNK_SIZE) }, (_, index) =>
    ids.slice(index * BULK_APPROVE_CHUNK_SIZE, (index + 1) * BULK_APPROVE_CHUNK_SIZE)
  );

// Sends the chunks one after another, so a large selection never floods company-manager. A chunk
// whose request fails is reported as failed for each of its ids, and the rest carry on.
const approveInChunks = async (recognitionIds: string[]) => {
  const results: BulkRecognitionApprovalResult[] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const chunk of toChunks(recognitionIds)) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await recognitionService.approveRecognitions({ recognitionIds: chunk });
      results.push(...response.results);
    } catch (error) {
      // A fresh error, so neither the request (its token) nor the recognition ids reach Sentry.
      Sentry.captureException(new Error('Bulk recognition approval request failed'), {
        tags: { flow: 'bulk-recognition-approval' },
        extra: {
          status: axios.isAxiosError(error) ? error.response?.status : undefined,
          recognitionCount: chunk.length,
        },
      });
      results.push(...chunk.map((recognitionId) => ({ recognitionId, result: 'FAILED' as const })));
    }
  }

  return results;
};

const useApproveRecognitions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: approveInChunks,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['getRecognition'] });
      queryClient.invalidateQueries({ queryKey: ['listPendingApprovals'] });
      queryClient.invalidateQueries({ queryKey: ['fetchRecognitionAudit'] });
      queryClient.invalidateQueries({ queryKey: ['fetchPots'] });
    },
  });
};

const useRejectRecognition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recognitionId,
      body,
    }: {
      recognitionId: string;
      body: RejectRecognitionRequest;
    }) => recognitionService.rejectRecognition(recognitionId, body),
    onSuccess: (_data, { recognitionId }) => {
      queryClient.invalidateQueries({ queryKey: ['getRecognition', recognitionId] });
      queryClient.invalidateQueries({ queryKey: ['listPendingApprovals'] });
    },
  });
};

type UseInfiniteListRecognitionsParams = Omit<FetchRecognitionRequest, 'page' | 'size'>;

const PAGE_SIZE = 6;

const useInfiniteListRecognitions = (
  params: UseInfiniteListRecognitionsParams = {},
  enabled = true
) => {
  const { groupIds, receiverIds, valueIds, query, createdFrom, createdTo } = params;

  return useInfiniteQuery<RecognitionPage, Error>({
    queryKey: ['listRecognitions', groupIds, receiverIds, valueIds, query, createdFrom, createdTo],
    queryFn: ({ pageParam = 0 }) =>
      recognitionService.fetchRecognitions({
        statuses: ['APPROVED'],
        groupIds,
        receiverIds,
        valueIds,
        query,
        createdFrom,
        createdTo,
        page: pageParam as number,
        size: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => {
      // A page that came back empty ends the feed whatever `total` claims. Without this,
      // an empty page plus a stale total leaves the load-more trigger permanently in
      // view, and it paginates in a loop.
      if (lastPage.list.length === 0) {
        return undefined;
      }

      const hasMore = (lastPage.pageNumber + 1) * lastPage.pageSize < lastPage.total;
      return hasMore ? lastPage.pageNumber + 1 : undefined;
    },
    enabled,
  });
};

const useHideRecognition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recognitionId: string) => recognitionService.hideRecognition(recognitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listRecognitions'] });
    },
  });
};

const useUnhideRecognition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recognitionId: string) => recognitionService.unhideRecognition(recognitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listRecognitions'] });
    },
  });
};

const useAddReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recognitionId, body }: { recognitionId: string; body: AddReactionRequest }) =>
      recognitionService.addReaction(recognitionId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listRecognitions'] });
    },
  });
};

const useDeleteReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recognitionId,
      reactionType,
    }: {
      recognitionId: string;
      reactionType: string;
    }) => recognitionService.deleteReaction(recognitionId, reactionType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listRecognitions'] });
    },
  });
};

const useRecognition = {
  useCreateRecognition,
  useListPendingApprovals,
  useGetRecognition,
  useUpdateRecognition,
  useDeleteRecognition,
  useApproveRecognition,
  useApproveRecognitions,
  useRejectRecognition,
  useInfiniteListRecognitions,
  useHideRecognition,
  useUnhideRecognition,
  useAddReaction,
  useDeleteReaction,
};

export default useRecognition;
